<template>
    <v-navigation-drawer
        v-model="internalDrawer"
        :rail="props.rail"
        :expand-on-hover="props.expandOnHover"
        :temporary="temporary"
        :location="location"
        :app="app"
    >
        <div class="d-flex align-center px-2 py-3">
            <v-img
                v-if="!props.rail"
                :src="require('@/public/assets/images/aquassist_reverse.svg')"
                alt="Aquassist Logo"
                min-height="50"
                max-height="50"
                @click="router.push('/clients')"
            />
            <v-img
                v-else
                :src="require('@/public/assets/images/aquassist-icon.svg')"
                alt="Aquassist Icon"
                min-height="30"
                max-height="30"
                class="mx-auto"
            />
        </div>
        <v-divider color="white"></v-divider>
        <v-list density="compact" nav>
            <SidebarItem
                v-for="(item, idx) in sidebarItems"
                :key="item.value || idx"
                :item="item"
                :is-railed="props.rail"
                @item-click="handleSidebarItemClick"
            />
        </v-list>

        <template v-slot:append>
            <div class="sidebar-footer">
                <v-divider></v-divider>
                <div class="px-2 py-3 d-flex justify-end">
                    <AQButton
                        v-if="!props.temporary"
                        size="small"
                        variant="text"
                        color="white"
                        :class="{ 'rotate-180': props.rail }"
                        class="toggle-btn"
                        @click="handleToggle"
                    >
                        <AQIcon :name="chevronIcon" />
                    </AQButton>
                </div>
            </div>
        </template>
    </v-navigation-drawer>
</template>

<script setup lang="ts">
    import { computed, PropType, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { useStore } from 'vuex';
    import SidebarItem, { type SidebarItem as SidebarItemInterface } from './SidebarItem.vue';
    import AQIcon from '@/components/shared/interactions/AQIcon.vue';

    const router = useRouter();

    type NavigationDrawerLocation = 'end' | 'start' | 'left' | 'top' | 'bottom' | 'right';

    const props = defineProps({
        modelValue: Boolean,
        items: {
            type: Array as PropType<SidebarItemInterface[]>,
        },
        temporary: Boolean,
        location: String as PropType<NavigationDrawerLocation>,
        app: Boolean,
        rail: {
            type: Boolean,
            default: false,
        },
        expandOnHover: {
            type: Boolean,
            default: false,
        },
    });
    // Track the current active item's subitems if available
    const activeSubItems = ref<SidebarItemInterface[]>([]);

    // Computed property to return either the main items or subitems of the active item
    const sidebarItems = computed(() => {
        if (activeSubItems.value.length > 0) {
            return activeSubItems.value;
        }
        return props.items || [];
    });

    // Watch the route to update active subitems
    watch(
        () => router?.currentRoute?.value?.path || '',
        (newPath: string) => {
            // Skip if no path is available (e.g., in tests)
            if (!newPath) return;

            // Reset to main items when navigating away
            const currentItems = props.items || [];
            const matchingItem = findMatchingItem(currentItems, newPath);

            // If we find a matching item with subitems, use those
            if (matchingItem && matchingItem.subItems?.length) {
                activeSubItems.value = matchingItem.subItems;
                return;
            }

            // Check if we're in a nested route structure
            for (const item of currentItems) {
                if (item.subItems?.length) {
                    // Check if any of the subitems match the current path
                    const matchingSubItem = findMatchingItem(item.subItems, newPath);
                    if (matchingSubItem) {
                        // If we're in a nested route, show all subitems of the parent
                        activeSubItems.value = item.subItems;
                        return;
                    }
                }
            }

            // If no matches found, reset to empty
            activeSubItems.value = [];
        },
        { immediate: true },
    );

    // Helper function to find a matching item based on path
    function findMatchingItem(
        items: SidebarItemInterface[],
        path: string,
    ): SidebarItemInterface | null {
        for (const item of items) {
            if (item.path && path.startsWith(item.path)) {
                return item;
            }
            // Check if any of the subitems match the path
            if (item.subItems?.length) {
                const matchingSubItem = findMatchingItem(item.subItems, path);
                if (matchingSubItem) {
                    return item; // Return the parent item that has the matching subitem
                }
            }
        }
        return null;
    }

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
        (e: 'item-click'): void;
    }>();

    const internalDrawer = computed({
        get: () => props.modelValue,
        set: (val) => emit('update:modelValue', val),
    });

    const handleSidebarItemClick = () => {
        emit('item-click');
    };

    const chevronIcon = computed(() => (props.rail ? 'mdi-chevron-right' : 'mdi-chevron-left'));

    const store = useStore();
    const handleToggle = () => {
        store.dispatch('navigation/toggleSidebarBehavior');
    };
</script>
